import axios from 'axios';
import api from './api';

const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001';
const apiBaseUrl = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;

export interface DiscoveredTenant {
  tenantSlug: string;
  tenantName: string;
  logoUrl?: string;
  primaryColor?: string;
}

export interface DomainResolutionResult {
  tenantSlug: string;
  tenantName: string;
  companyName?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  faviconUrl?: string;
  domainType: string;
}

export interface PortalLoginRequest {
  email: string;
  password: string;
}

export interface PortalContact {
  contactId: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  customerId: string;
  customerName: string;
  companyName?: string;
  roleName?: string;
  permissions: string[];
}

export interface PortalLoginResponse extends PortalContact {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

export const authService = {
  async resolveCustomDomain(domain: string): Promise<DomainResolutionResult | null> {
    try {
      const response = await axios.get<DomainResolutionResult>(
        `${apiBaseUrl}/resolve-domain?domain=${encodeURIComponent(domain)}`
      );
      return response.data;
    } catch {
      return null;
    }
  },

  async discoverTenant(email: string): Promise<DiscoveredTenant | null> {
    try {
      const response = await axios.post<DiscoveredTenant>(
        `${apiBaseUrl}/portal/auth/discover`,
        { email },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // Store the discovered tenant slug
      localStorage.setItem('portal_tenant_slug', response.data.tenantSlug);

      return response.data;
    } catch {
      return null;
    }
  },

  async login(email: string, password: string, tenantSlug: string): Promise<PortalLoginResponse> {
    localStorage.setItem('portal_tenant_slug', tenantSlug);
    const response = await api.post<PortalLoginResponse>('/portal/auth/login', {
      email,
      password,
    });
    return response.data;
  },

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('portal_refresh_token');
    if (refreshToken) {
      try {
        await api.post('/portal/auth/logout', { refreshToken });
      } catch {
        // Ignore logout errors
      }
    }
    localStorage.removeItem('portal_access_token');
    localStorage.removeItem('portal_refresh_token');
    localStorage.removeItem('portal_contact');
  },

  async getCurrentContact(): Promise<PortalContact> {
    const response = await api.get<PortalContact>('/portal/auth/me');
    return response.data;
  },

  async forgotPassword(email: string): Promise<void> {
    await api.post('/portal/auth/forgot-password', { email });
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await api.post('/portal/auth/reset-password', { token, newPassword });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/portal/auth/change-password', { currentPassword, newPassword });
  },
};
